import { Box, Center, Group, useMantineTheme } from '@mantine/core';
import { IconPencil, IconWorld } from '@tabler/icons-react';
import { usePickGlobalPermissions } from 'components/modals/pick-global-permissions-modal/PickGlobalPermissionsProvider';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import Button from 'components/Button';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import {
  ALL_ORGANIZATION_MODULES_ENABLED,
  isPermissionEnabledForOrganization,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';
import { useEffect, useMemo } from 'react';

interface GlobalPermissionsForUserProps {
  permissions: number[];
  onChange: (permissions: number[]) => void;
  moduleFlags?: OrganizationModuleFlags;
}

/**
 * Renders direct global permissions in the same summary-card style as entity permissions.
 */
export default function GlobalPermissionsForUser({
  permissions,
  onChange,
  moduleFlags = ALL_ORGANIZATION_MODULES_ENABLED,
}: GlobalPermissionsForUserProps) {
  const theme = useMantineTheme();
  const { pickGlobalPermissions } = usePickGlobalPermissions();
  const { getPermission } = usePermissionDataProvider();
  const availablePermissions = useMemo(
    () =>
      permissions.filter((permissionId) => {
        const permission = getPermission(permissionId);
        return permission && isPermissionEnabledForOrganization(permission.slug, moduleFlags);
      }),
    [getPermission, moduleFlags, permissions]
  );

  useEffect(() => {
    if (availablePermissions.length !== permissions.length) {
      onChange(availablePermissions);
    }
  }, [availablePermissions, onChange, permissions]);

  /**
   * Opens the modal for editing direct global permissions.
   */
  function updatePermissions() {
    pickGlobalPermissions({
      title: 'Globálne oprávnenia',
      initialPermissions: availablePermissions,
      onPick: onChange,
      moduleFlags,
    });
  }

  return (
    <Box>
      <Group
        style={{
          border: `1px solid ${theme.colors.gray[2]}`,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          alignContent: 'center',
        }}
        bg="gray.0"
        py={12}
        px={16}
        position="apart"
        mih={56}
        spacing={16}
      >
        <Group spacing={12}>
          <Center bg="gray.7" style={{ borderRadius: '999px' }} w={36} h={36} aria-hidden>
            <IconWorld stroke="1.5" size={20} color={theme.colors.gray[0]} />
          </Center>
          <Group spacing={8} maw={380}>
            <P1Medium color="gray.8">Globálne oprávnenia</P1Medium>
            <P2Regular>Priamo pridelené oprávnenia</P2Regular>
          </Group>
        </Group>
        <Button
          variant="subtle-gray"
          size="sm"
          leftIcon={<IconPencil stroke="1.5" color={theme.colors.gray[7]} />}
          onClick={updatePermissions}
        >
          Upraviť oprávnenia
        </Button>
      </Group>
      <Group
        style={{
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
          borderLeft: `1px solid ${theme.colors.gray[2]}`,
          borderRight: `1px solid ${theme.colors.gray[2]}`,
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
        }}
        p={16}
        spacing={8}
      >
        {availablePermissions.length > 0 ? (
          availablePermissions.map(
            (permissionId) =>
              getPermission(permissionId) && (
                <Box
                  style={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: '4px' }}
                  bg="white"
                  px={8}
                  key={permissionId}
                >
                  <P2Regular>{getPermission(permissionId)?.permissionName}</P2Regular>
                </Box>
              )
          )
        ) : (
          <P2Regular color="gray.6">Používateľ zatiaľ nemá žiadne globálne oprávnenia.</P2Regular>
        )}
      </Group>
    </Box>
  );
}
